import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useMemo, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Tooltip, Typography } from '@mui/material';
import { SecondaryGrid } from '../../../../components/common/grid/SecondaryGrid';
import { TableHeader } from '../../../../components/common/header/tableHeader/TableHeader';
import { Eye, DocumentText1, Sms } from 'iconsax-react';
import { InvoiceEmailDialog } from '../dialog/InvoiceEmailDialog';
import { InternalCompany } from '@application/common/enums/internal-company';
import InternalCompanyApi from '@infrastructure/internalcompany-api';
import { getInternalCompaniesAsync, createOrUpdateInternalCompanyAsync } from '@application/features/internalCompany/internalCompanyThunk';
export const InvoiceEmailModule = () => {
    const dispatch = useAppDispatch();
    const internalCompanies = useAppSelector((state) => state.internalCompany.internalCompanies);
    const [details, setDetails] = useState(null);
    const [detailsDepartment, setDetailsDepartment] = useState(null);
    const [retrying, setRetrying] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState('add');
    const [selectedItem, setSelectedItem] = useState(null);
    const rows = useMemo(() => {
        const byCompany = new Map((internalCompanies ?? []).map((x) => [x.company, x]));
        const labels = Array.from(new Set([...Object.values(InternalCompany), ...(internalCompanies ?? []).map(x => x.company)]));
        return labels.map(value => {
            const saved = byCompany.get(value);
            return {
                id: saved?.id ?? value, departmentId: saved?.id, department: value, senderEmail: saved?.email ?? '',
                revision: saved?.faqtoringRevision ?? 0, legalName: saved?.legalName ?? '',
                registrationNumber: saved?.registrationNumber ?? '', countryCode: saved?.countryCode ?? '',
                legalRegister: saved?.legalRegister ?? '', paused: saved?.faqtoringPaused ?? false,
                faqtoringStatus: saved?.faqtoringStatus ?? 'Gegevens ontbreken', faqtoringReason: saved?.faqtoringReason ?? 'Juridische bedrijfsgegevens aanvullen.'
            };
        });
    }, [internalCompanies]);
    useEffect(() => {
        let stopped = false;
        let timer;
        const refresh = async () => {
            await dispatch(getInternalCompaniesAsync());
            if (!stopped)
                timer = setTimeout(refresh, 30000);
        };
        void refresh();
        return () => { stopped = true; clearTimeout(timer); };
    }, [dispatch]);
    const openEdit = (item) => {
        setSelectedItem(item);
        setDialogMode('edit');
        setDialogOpen(true);
    };
    const closeDialog = () => {
        setDialogOpen(false);
        setSelectedItem(null);
    };
    const onSave = async (item) => {
        await dispatch(createOrUpdateInternalCompanyAsync({
            id: item.departmentId, expectedRevision: item.revision,
            company: item.department, email: item.senderEmail,
            legal: { legalName: item.legalName ?? '', registrationNumber: item.registrationNumber ?? '',
                countryCode: item.countryCode ?? '', legalRegister: item.legalRegister ?? '', paused: item.paused ?? false }
        })).unwrap();
        closeDialog();
    };
    const columns = [
        {
            field: 'department',
            headerName: 'Internal Department',
            flex: 1,
            minWidth: 200,
            renderHeader: () => _jsx(TableHeader, { icon: DocumentText1, text: 'Internal Department' })
        },
        {
            field: 'senderEmail',
            headerName: 'Invoice recipient',
            flex: 1,
            minWidth: 200,
            renderHeader: () => _jsx(TableHeader, { icon: Sms, text: 'Invoice recipient' })
        },
        {
            field: 'faqtoringStatus', headerName: 'Faqtoring', flex: 1.4, minWidth: 220,
            renderCell: params => _jsx(Tooltip, { title: params.row.faqtoringReason ?? '', children: _jsx(Typography, { variant: 'body2', noWrap: true, children: params.row.faqtoringStatus }) })
        },
        {
            field: 'actions',
            headerName: 'Actions',
            headerAlign: 'center',
            align: 'center',
            flex: 0.8,
            minWidth: 150,
            renderHeader: () => _jsx(TableHeader, { icon: DocumentText1, text: 'Actions' }),
            renderCell: (params) => (_jsxs(Box, { sx: { display: 'flex', gap: 1, justifyContent: 'center' }, children: [_jsx(IconButton, { onClick: () => openEdit(params.row), size: 'small', children: _jsx(Eye, {}) }), params.row.departmentId && _jsx(Button, { size: 'small', onClick: async () => { setDetailsDepartment(params.row.departmentId); try {
                            const result = await InternalCompanyApi.details(params.row.departmentId);
                            setDetails(JSON.stringify(result.data, null, 2));
                        }
                        catch {
                            setDetails('Beheerdetails konden niet worden opgehaald.');
                        } }, children: "Details" })] }))
        }
    ];
    return (_jsxs(_Fragment, { children: [_jsx(SecondaryGrid, { columns: columns, rows: rows }), _jsxs(Dialog, { open: details !== null, onClose: () => setDetails(null), maxWidth: 'md', fullWidth: true, children: [_jsx(DialogTitle, { children: "Faqtoring \u2014 beheerdetails" }), _jsx(DialogContent, { children: _jsx(Box, { component: 'pre', sx: { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: 12 }, children: details }) }), _jsxs(DialogActions, { children: [_jsx(Button, { disabled: retrying || !detailsDepartment, onClick: async () => {
                                    if (!detailsDepartment)
                                        return;
                                    setRetrying(true);
                                    try {
                                        await InternalCompanyApi.retry(detailsDepartment);
                                        const result = await InternalCompanyApi.details(detailsDepartment);
                                        setDetails(JSON.stringify(result.data, null, 2));
                                        await dispatch(getInternalCompaniesAsync());
                                    }
                                    catch {
                                        setDetails('Opnieuw controleren is niet gelukt. Probeer later opnieuw.');
                                    }
                                    finally {
                                        setRetrying(false);
                                    }
                                }, children: "Opnieuw controleren" }), _jsx(Button, { onClick: () => setDetails(null), children: "Sluiten" })] })] }), _jsx(InvoiceEmailDialog, { open: dialogOpen, handleClose: closeDialog, type: dialogMode, item: selectedItem, onSave: onSave }, `invoice-email-${selectedItem?.id ?? 'add'}`)] }));
};
